import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/shadcn/table.tsx"
import { useGetAnimalsQuery } from "@/features/farm/api/animals-api.ts"
import { useGetTaskCategoriesQuery } from "@/features/tasks/api/task-categories-api.ts"
import { useToggleTaskStatusMutation } from "@/features/tasks/api/tasks-api.ts"
import { getTaskTableColumns } from "@/features/tasks/components/table/task-columns.tsx"
import { TASKS_LIST_VIEW } from "@/features/tasks/routes/route-list.ts"
import { type Task } from "@/features/tasks/types/task.ts"

interface TaskTableProps {
  tasks: Task[]
}

const TaskTable: React.FC<TaskTableProps> = ({ tasks }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [sorting, setSorting] = React.useState<SortingState>([])

  const { data: animals = [] } = useGetAnimalsQuery()
  const { data: categories = [] } = useGetTaskCategoriesQuery()
  const [toggleTaskStatus] = useToggleTaskStatusMutation()

  const columns = useMemo(
    () => getTaskTableColumns({ t, animals, categories, toggleTaskStatus }),
    [t, animals, categories, toggleTaskStatus],
  )

  const table = useReactTable({
    data: tasks,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={
                    header.column.getCanSort()
                      ? "cursor-pointer select-none hover:bg-gray-50"
                      : ""
                  }
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div className="flex items-center gap-2">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {header.column.getIsSorted() && (
                      <span>
                        {header.column.getIsSorted() === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => navigate(`${TASKS_LIST_VIEW}/${row.original.id}`)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export default TaskTable
